import { List, ListItem, makeStyles, withStyles } from '@material-ui/core';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '~components/circular-progress';
import { Divider } from '~components/divider';
import Input from '~components/input/input';
import { TooltipClick } from '~components/tooltip';
import Typography from '~components/typography';
import { ArrowV } from '~svg-components';
import useForkRef from '~utils/use-fork-ref';

const StyledListItem = withStyles((theme) => ({
  root: {
    ...theme.typography.dropdown,
    padding: '6px 12px',
    color: theme.colors.dark,
    flexDirection: 'column',
    alignItems: 'flex-start',
    '&:hover:not($disabled)': {
      backgroundColor: theme.colors.brandBg,
    },

    '&$selected': {
      backgroundColor: 'unset',
      color: theme.colors.brandAction,
    },
  },
  container: {},
  focusVisible: {},
  dense: {},
  alignItemsFlexStart: {},
  disabled: {},
  divider: {},
  gutters: {},
  button: {},
  secondaryAction: {},
  selected: {},
}))(ListItem);

const useStyles = makeStyles(
  (theme) => ({
    Input: {
      cursor: 'pointer',
      '& > .MuiInputBase-input': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
    },

    keyboardArrow: {
      marginRight: 12,
      color: theme.colors.grey66,
      transform: 'rotate(90deg)',
      fontSize: 10,
      transition: 'transform 150ms cubic-bezier(0.65, 0.05, 0.36, 1)',
    },
    keyboardArrowUp: {
      transform: 'rotate(270deg)',
    },

    list: {
      paddingTop: 4,
      paddingBottom: 4,
      overflowY: 'auto',
      maxHeight: 256,

      '& > .Mui-selected *': {
        color: theme.colors.brandAction,
      },
    },

    noOptions: {
      ...theme.typography.dropdown,
      padding: '6px 12px',
      color: theme.colors.grey99,
    },

    loading: {
      marginRight: 4,
    },

    divider: {
      marginLeft: 8,
      marginRight: 8,
    },

    tooltipPlacementBottom: {
      margin: '2px 0 0',
    },
    listWrapper: {
      border: `1px solid ${theme.colors.greyE8}`,
      borderRadius: theme.borderRadius.small,
    },
  }),
  { name: 'SelectPopper' }
);

const SelectPopper = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const rootRef = useRef();
  const {
    defaultValue,
    value: valueProp,
    onChange,
    name,
    placeholder,
    options,
    renderValue,
    InputProps,
    ListProps,
    disabled,
    loading,
    loadingChildren,
    actionOptions,
    onOptionsText,
    ...otherProp
  } = props;
  const isControlled = useRef(valueProp !== null && valueProp !== undefined);

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = isControlled.current ? valueProp : internalValue;

  const inputValue = renderValue
    ? renderValue(value)
    : options.find((item) => item.value === value)?.children ?? '';

  const [width, setWidth] = useState();
  useEffect(() => {
    const offsetWidth = rootRef.current?.offsetWidth;
    if (rootRef.current && width !== offsetWidth) {
      setWidth(offsetWidth);
    }
  }, [width]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputClick = (e) => {
    if (!disabled) {
      if (!open) {
        handleOpen(e);
      } else {
        handleClose(e);
      }
    }
  };

  const handleListItemClick = (e, option) => {
    const event = { ...e, target: { ...e.target, value: option.value, name } };
    if (onChange) onChange(event, option);
    if (!isControlled.current) setInternalValue(option.value);
  };

  const classes = useStyles({ width });

  const renderListItems = (items) => {
    return items.map((item, index) => {
      const itemValue = typeof item.value === 'object' ? item.value?.id : item.value;
      const itemKey = itemValue ?? index;

      if (item.type === 'other') {
        const { type, ...otherItems } = item;
        return <StyledListItem key={itemKey} {...otherItems} disabled style={{ opacity: 1 }} />;
      }

      return (
        <StyledListItem
          key={itemKey}
          button
          disableRipple
          {...item}
          onClick={(e) => {
            if (item.onClick) {
              item.onClick(e, item);
            } else {
              handleListItemClick(e, item);
            }
            setOpen(false);
          }}
          selected={item.value === value}
          data-value={item.value}
          value={undefined}
        >
          <Typography>{item?.children}</Typography>
          {item?.description && (
            <Typography style={{ color: '#999' }}>{item?.description}</Typography>
          )}
        </StyledListItem>
      );
    });
  };

  const renderNoOption = () => {
    return (
      <ListItem className={classes.noOptions}>
        {onOptionsText ?? t('input_search_select.no_options')}
      </ListItem>
    );
  };

  return (
    <TooltipClick
      open={open}
      title={
        <div className={classes.listWrapper}>
          <List
            {...ListProps}
            className={classnames(classes.list, ListProps.className)}
            style={{ width: width - 2, ...ListProps.style }}
          >
            {options?.length > 0 ? renderListItems(options) : renderNoOption()}
          </List>
          {actionOptions && (
            <>
              <Divider className={classes.divider} />
              <List
                {...ListProps}
                className={classnames(classes.list, ListProps.className)}
                style={{ width: width - 2, ...ListProps.style }}
              >
                {renderListItems(actionOptions)}
              </List>
            </>
          )}
        </div>
      }
      onClose={handleClose}
      ref={useForkRef(ref, rootRef)}
      disabled={disabled}
      classes={{ tooltipPlacementBottom: classes.tooltipPlacementBottom }}
      {...otherProp}
    >
      <Input
        value={inputValue}
        onClick={handleInputClick}
        endAdornment={
          loading ? (
            loadingChildren || <CircularProgress size={16} className={classes.loading} />
          ) : (
            <ArrowV
              className={classnames(classes.keyboardArrow, { [classes.keyboardArrowUp]: open })}
            />
          )
        }
        autoComplete="off"
        readOnly
        inputComponent="div"
        {...InputProps}
        inputProps={{
          children:
            inputValue === '' ? (
              <Typography themeColor="grey99">{placeholder}</Typography>
            ) : (
              inputValue
            ),
          ...InputProps.inputProps,
        }}
        className={classnames(classes.Input, InputProps.className)}
      />
    </TooltipClick>
  );
});

SelectPopper.propTypes = {
  defaultValue: PropTypes.any,
  value: PropTypes.any,
  onChange: PropTypes.func,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      children: PropTypes.node.isRequired,
    })
  ).isRequired,
  renderValue: PropTypes.func,
  InputProps: PropTypes.object,
  ListProps: PropTypes.object,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  loadingChildren: PropTypes.node,
  actionOptions: PropTypes.array,
  onOptionsText: PropTypes.node,
};

SelectPopper.defaultProps = {
  defaultValue: undefined,
  value: undefined,
  onChange: undefined,
  name: undefined,
  placeholder: undefined,
  renderValue: undefined,
  InputProps: {},
  ListProps: {},
  disabled: false,
  loading: false,
  loadingChildren: undefined,
  actionOptions: undefined,
  onOptionsText: undefined,
};

export default SelectPopper;
